import React, { useCallback, useMemo, useState } from 'react'

import { generatePath, useHistory, useParams } from 'react-router-dom'

import {
  AiInventoryDaiChat,
  AiInventoryDaiReview,
  Container,
  Page,
} from 'components'

import useAuthGuest from 'hooks/useAuthGuest'
import useSnackbar from 'hooks/useSnackbar'
import useAiInventoryDaiChat, {
  mapAiInventoryToPanelItem,
} from 'hooks/useAiInventoryDaiChat'
import * as service from 'service'

import { routes } from 'GuestRoutes'

const APP_NAME = 'ai_inventory_guest_agent'
const CHAT_TITLE = 'Mapeamento de IA do seu setor'

const AiInventoryDaiRegistration = () => {
  const history = useHistory()
  const { organizationSlug } = useParams()
  const { user } = useAuthGuest()
  const snackbar = useSnackbar()

  // Revisão é uma view do MESMO componente (não navega) — assim a sessão de
  // chat (mensagens, item ativo, contexto do agente) não é perdida ao voltar.
  const [reviewing, setReviewing] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [concluding, setConcluding] = useState(false)

  // O departamento do convidado também é resolvido server-side via token; aqui
  // ele só enriquece o contexto da sessão quando disponível.
  const sessionState = useMemo(
    () =>
      user?.departmentId ? { departmentId: user.departmentId } : undefined,
    [user],
  )

  const loadItems = useCallback(async () => {
    // detailed=true → serializer completo (finalidades, modo, intervenção, risco).
    const response = await service.guestPortal.aiInventory.list({
      detailed: true,
      perPage: 1000,
    })
    const inventories = response?.data?.aiInventoryItems || []
    return inventories.map(mapAiInventoryToPanelItem)
  }, [])

  // Busca incremental de um único item (após cada turno do chat) — evita
  // recarregar a listagem detalhada inteira a cada mensagem.
  const loadItem = useCallback(async (aiInventoryId) => {
    const response = await service.guestPortal.aiInventory.show(aiInventoryId)
    const inventory = response?.data?.aiInventoryItem
    return inventory ? mapAiInventoryToPanelItem(inventory) : null
  }, [])

  const {
    messages,
    items,
    activeItemId,
    sendMessage,
    isSending,
    abortMessage,
  } = useAiInventoryDaiChat({
    chatClient: service.dai.guestChat,
    appName: APP_NAME,
    userId: String(user?.id),
    sessionState,
    loadItems,
    loadItem,
  })

  const goToList = () =>
    history.push(generatePath(routes.aiInventory.list, { organizationSlug }))

  const handleConclude = async () => {
    setConcluding(true)
    try {
      await service.guestPortal.aiInventory.confirmVeracity(
        items.map((item) => item.id),
      )
      history.push(generatePath(routes.aiInventory.list, { organizationSlug }))
    } catch {
      snackbar.open({
        message: 'Não foi possível concluir o cadastro. Tente novamente.',
        variant: 'error',
      })
      setConcluding(false)
    }
  }

  // Só oferece "revisar e concluir" quando já há ao menos um item cadastrado.
  const showConclusion = items.length > 0

  return (
    <Page title="Cadastro guiado pela DAI">
      <Container
        maxWidth={false}
        spacingXl={3}
        spacingLg={3}
        spacingMd={3}
        spacingSm={2}
      >
        {reviewing ? (
          <AiInventoryDaiReview
            items={items}
            confirmed={confirmed}
            onConfirmChange={setConfirmed}
            onBack={() => setReviewing(false)}
            onConclude={handleConclude}
            loading={concluding}
            fullBleed
            showRisk={false}
          />
        ) : (
          <AiInventoryDaiChat
            title={CHAT_TITLE}
            messages={messages}
            items={items}
            activeItemId={activeItemId}
            showConclusion={showConclusion}
            onConclude={() => setReviewing(true)}
            onSend={sendMessage}
            onBack={goToList}
            isSending={isSending}
            abortMessage={abortMessage}
            fullBleed
            showRisk={false}
            userName={user?.responsibleName}
          />
        )}
      </Container>
    </Page>
  )
}

export default AiInventoryDaiRegistration
